import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { keyframes, styled } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Guid from "_shared/guids/guid";
import { CanceledError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency, Masked } from "work/_shared/masked-fields";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import {
  FeeScheduleTemplateOrderField,
  FeeScheduleTemplateOrderParameters,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import {
  FeeScheduleTagChip,
  TemplateList,
  TemplateLoader,
} from "work/fee-schedule-templates/_views/_shared/embedded-containers";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues, { ProposalIssue, ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import ProposalField, { FreelyPatchableFields } from "work/proposals/proposal-fields/proposal-field";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FeeScheduleRedlineSelection from "work/proposals/redlining/field-redlines/_views/fee-schedule-redline-selection";
import ProposalRedline from "work/proposals/redlining/proposal-redline";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ListContainer = styled(Grid)(() => ({
  height: "100%",
  minHeight: "25rem",
}));
const TemplateContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "100%",
  },
}));
const RedlineCategorySelection = styled(FeeScheduleRedlineSelection)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  flex: 1,
  height: "100%",
  marginLeft: theme.spacing(0),
}));
const IssueContainer = styled("div")<{ active?: boolean }>(({ theme, active }) => ({
  animation: active ? `${pulseBorder} 1.5s ease-in-out infinite` : "none",
  margin: active ? theme.spacing(1) : "initial",
  marginLeft: 0,
  marginBottom: theme.spacing(1),
  padding: active ? theme.spacing(1) : "initial",
  borderRadius: theme.shape.borderRadius,
}));

type FeeScheduleTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  commentThreads: CommentThread[];
  disableEditing?: boolean;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField?: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (field: ProposalField, name?: string) => void;

  fieldsToPatch?: FreelyPatchableFields;

  onFeeScheduleTemplatesUpdated: (templateIds: Guid[], newProposalRedline?: ProposalRedline) => void;
};

const FeeScheduleTab = (props: Readonly<FeeScheduleTabProps>) => {
  const {
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,

    fieldsToPatch,

    onFeeScheduleTemplatesUpdated,
  } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [expandedTemplates, setExpandedTemplates] = useState<Set<FeeScheduleTemplate>>(new Set());
  const [templateFeeSchedules, setTemplateFeeSchedules] = useState<FeeScheduleTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Guid[]>([]);
  const [paginatedParameters, setPaginatedParameters] = useState<PaginationParameters>();

  const issues: ProposalIssue[] = React.useMemo(() => {
    return props.issues?.entries.filter((issue) => [
      ProposalField.FeeSchedule,
      ProposalField.FeeScheduleCategory(issue.field.id ?? Guid.EMPTY)
    ].includes(issue.field)) ?? [];
  }, [props.issues?.entries]);

  const isLargeDownDisplay = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const session = useSession();

  useEffect(() => {
    let abortController = new AbortController();
    void getFeeScheduleTemplates(abortController);
    setSelectedTemplateIds(fieldsToPatch?.feeScheduleTemplateIds ?? []);

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, []);

  async function getFeeScheduleTemplates(abortController: AbortController) {
    const entityId = session.user!.legalEntityId;
    if (!entityId) return;

    setIsLoading(true);
    try {
      const filterParameters = new FeeScheduleTemplateFilterParameters({ entityId });

      const orderParameters = new FeeScheduleTemplateOrderParameters({
        orderBy: FeeScheduleTemplateOrderField.Name,
        orderDir: OrderDirection.Descending,
      });

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery({
        filterParameters,
        orderParameters,
        paginationParameters: paginatedParameters ?? PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
      });

      const feeScheduleService = new FeeScheduleTemplateQueryAPIService(session);
      const paginatedTemplateJSON = await feeScheduleService.getFeeScheduleTemplatesByQuery(
        templateQuery,
        abortController
      ); // TODO: use redux store
      setTemplateFeeSchedules(FeeScheduleTemplate.fromJSONArray(paginatedTemplateJSON.data));
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar("Failed to load fee schedule templates.", {
        variant: "error",
      });
    } finally {
      setPaginatedParameters((prev) => {
        if (prev) {
          return PaginationParameters.create({ pageIndex: prev.pageIndex, pageSize: prev.pageSize });
        }
      });
      setIsLoading(false);
    }
  }

  function handleTemplateExpanded(template: FeeScheduleTemplate) {
    let updatedExpandedTemplates = new Set(expandedTemplates);
    if (updatedExpandedTemplates.has(template)) {
      updatedExpandedTemplates.delete(template);
    } else {
      updatedExpandedTemplates.add(template);
    }
    setExpandedTemplates(updatedExpandedTemplates);
  }

  function handleTemplateSelectionToggled(template: FeeScheduleTemplate) {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot modify fee schedule when it is masked.");
    }

    try {
      if (!template.id) {
        // noinspection ExceptionCaughtLocallyJS
        throw new Error("No template selected");
      }

      let updatedSelectedTemplateIds = [...selectedTemplateIds];
      const isAlreadySelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));

      let newProposalRedline: ProposalRedline | undefined = undefined;

      if (isAlreadySelected) {
        updatedSelectedTemplateIds = updatedSelectedTemplateIds.filter((id) => !id.isEqualTo(template.id));
      } else {
        updatedSelectedTemplateIds.push(template.id);

        let newFeeScheduleRedline: FeeScheduleRedline = proposalRedline.feeSchedule;
        for (const category of template.categories) {
          newFeeScheduleRedline = newFeeScheduleRedline.addEntry(category);
        }
        newProposalRedline = proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline);
      }

      setSelectedTemplateIds(updatedSelectedTemplateIds);
      onFeeScheduleTemplatesUpdated(updatedSelectedTemplateIds, newProposalRedline);
      onCommentsClicked(ProposalField.FeeSchedule, undefined);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to apply template.", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleTemplateCategoryToggled(
    event: React.MouseEvent,
    templateCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null
  ) {
    event.stopPropagation();
    if (Masked.isMasked(proposalRedline.feeSchedule)) throw new Error("Cannot modify fee schedule when it is masked.");

    if (!templateCategory) return;

    if (getIsCategoryAlreadySelected(templateCategory)) {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.removeEntryByFieldId(templateCategory.id)
      );

      onProposalRedlineChange(updatedRedline, null);
    } else {
      const updatedRedline = proposalRedline.updateFeeScheduleRedline(
        proposalRedline.feeSchedule.addEntry(templateCategory)
      );
      onProposalRedlineChange(updatedRedline, null);
    }
    onCommentsClicked(ProposalField.FeeSchedule, undefined);
  }

  function getIsCategoryAlreadySelected(
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null
  ): boolean {
    if (Masked.isMasked(proposalRedline.feeSchedule)) {
      throw new Error("Cannot check category when fee schedule is masked.");
    }

    if (!category) return false;
    return proposalRedline.feeSchedule.redlines.some(
      (categoryRedline) => categoryRedline.field.id?.isEqualTo(category.id) && categoryRedline.currentEntry !== null
    );
  }

  function renderTemplates() {
    return (
      <TemplateList dense subheader={<ListSubheader>Templates</ListSubheader>}>
        {templateFeeSchedules.length === 0 && (
          <>
            {isLoading ? (
              <TemplateLoader size={32} thickness={4} disableShrink />
            ) : (
              <ListItem key="noTemplates">
                <ListItemText>No Templates</ListItemText>
              </ListItem>
            )}
          </>
        )}
        {templateFeeSchedules.map((template: FeeScheduleTemplate) => {
          const isSelected = selectedTemplateIds.some((id) => id.isEqualTo(template.id));
          const feeScheduleCategories = [...template.categories];
          const indeterminate =
            !isSelected && feeScheduleCategories.some((category) => category && getIsCategoryAlreadySelected(category));

          return (
            <div key={template.id?.value}>
              <ListItem
                secondaryAction={
                  <Tooltip title={`${expandedTemplates.has(template) ? "Hide" : "Show"} Categories`}>
                    <span>
                      <IconButton edge="end" onClick={() => handleTemplateExpanded(template)} size="medium">
                        {expandedTemplates.has(template) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemIcon>
                  <Tooltip title={isSelected ? "Deselect Template" : "Select Template"}>
                    <span>
                      <Checkbox
                        edge="start"
                        color="primary"
                        checked={isSelected}
                        indeterminate={indeterminate}
                        tabIndex={-1}
                        disableRipple
                        onChange={() => handleTemplateSelectionToggled(template)}
                      />
                    </span>
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={template.name}
                  secondary={template.tags.map((tag) => (
                    <FeeScheduleTagChip key={tag.value} size="small" label={tag.value} />
                  ))}
                />
              </ListItem>
              <Collapse in={expandedTemplates.has(template)} timeout="auto" style={{ backgroundColor: "#FAFAFA" }}>
                <Divider />
                <List disablePadding dense>
                  {renderTemplateCategories(template)}
                </List>
                <Divider />
              </Collapse>
            </div>
          );
        })}
      </TemplateList>
    );
  }

  function renderTemplateCategories(template: FeeScheduleTemplate) {
    return template.categories.map((category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => (
      <ListItem
        key={category.name.value}
        dense
        onClick={(event) => handleTemplateCategoryToggled(event, category)}
        secondaryAction={
          <Checkbox
            edge="end"
            color="primary"
            size="small"
            checked={getIsCategoryAlreadySelected(category)}
            tabIndex={-1}
            disableRipple
            onClick={(event) => handleTemplateCategoryToggled(event, category)}
          />
        }
      >
        <ListItemText
          primary={category.name.value}
          secondary={
            <>
              {category.description?.value}
              <br />
              {category instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : category.feeString}
            </>
          }
        />
      </ListItem>
    ));
  }

  if (Masked.isMasked(proposalRedline.feeSchedule)) {
    return "You do not have permission to view the fee schedule.";
  }

  return (
    <ListContainer container direction="row">
      {!disableEditing && (
        <TemplateContainer>
          {renderTemplates()}
          {isLargeDownDisplay && <Divider />}
        </TemplateContainer>
      )}
      <Grid size="grow">
        {(issues?.length ?? 0) > 0 && (
          <IssueContainer>
            {issues?.map((issue) => (
              <Typography
                key={issue.field?.cacheKey}
                color={issue.level === ProposalIssueLevel.Critical ? "error" : "warning"}
                variant="body1"
              >
                {issue.description}
              </Typography>
            ))}
          </IssueContainer>
        )}
        <RedlineCategorySelection
          activeReviewField={activeReviewField}
          commentThreads={commentThreads}
          disableEditing={disableEditing}
          feeScheduleRedline={proposalRedline.feeSchedule}
          onCommentsClicked={onCommentsClicked}
          onFeeScheduleRedlineChange={(
            newFeeScheduleRedline: FeeScheduleRedline,
            traversalFieldOverride?: ProposalField | null
          ) => {
            try {
              onProposalRedlineChange(
                proposalRedline.updateFeeScheduleRedline(newFeeScheduleRedline),
                traversalFieldOverride
              );
            } catch (error) {
              console.error(error);
              enqueueSnackbar("Failed to update fee schedule. Please try again", {
                variant: "error",
              });
            }
          }}
          onCategoryClicked={(categoryId: Guid) => {
            onTraverseToNewField?.(ProposalField.FeeScheduleCategory(categoryId));
          }}
        />
      </Grid>
    </ListContainer>
  );
};

export default FeeScheduleTab;
